<template>
  <div class="hs-page">

    <!-- ── HERO ─────────────────────────────────────────────── -->
    <section class="hs-hero">
      <!-- Animated background orbs -->
      <div class="hs-hero__bg" aria-hidden="true">
        <div class="hs-orb hs-orb--1"></div>
        <div class="hs-orb hs-orb--2"></div>
        <div class="hs-orb hs-orb--3"></div>
        <div class="hs-orb hs-orb--4"></div>
        <div class="hs-hero__grid"></div>
      </div>

      <div class="hs-hero__inner" :class="{ 'is-visible': isVisible.hero }">
        <p class="hs-eyebrow">Behavior-Centered Defensive Driving</p>
        <h1 class="hs-display">HABITSAFE</h1>
        <p class="hs-hero__sub">
          Transforming road safety through reflective learning — making safe driving instinctive, not delayed.
        </p>
        <div class="hs-hero__actions">
          <router-link to="/contact" class="hs-btn hs-btn--primary">
            Get Started
            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
          <button @click="scrollToSection(challengeSection)" class="hs-btn hs-btn--ghost hs-btn--ghost-hero">Learn More</button>
        </div>
      </div>
      <div class="hs-hero__scroll-hint">
        <span></span>
      </div>
    </section>

    <!-- ── STATS ─────────────────────────────────────────────── -->
    <section class="hs-stats">
      <div class="hs-container">
        <div class="hs-stats__grid">
          <div v-for="(stat, i) in stats" :key="i"
               class="hs-stat"
               :class="{ 'is-visible': isVisible.stats }"
               :style="{ transitionDelay: `${i * 120}ms` }">
            <span class="hs-stat__num">{{ stat.value }}</span>
            <span class="hs-stat__label">{{ stat.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CHALLENGE ──────────────────────────────────────────── -->
    <section ref="challengeSection" class="hs-section hs-section--alt">
      <div class="hs-container">
        <div class="hs-section__header"
             :class="{ 'is-visible': isVisible.challenge }">
          <h2 class="hs-heading">The Global Road Safety Challenge</h2>
          <p class="hs-body-lg">
            Despite advances in technology and infrastructure, road crashes remain a leading cause of death worldwide.
          </p>
        </div>
        <div class="hs-cards-2">
          <div v-for="(card, i) in challengeCards" :key="i"
               class="hs-card"
               :class="{ 'is-visible': isVisible.challenge }"
               :style="{ transitionDelay: `${i * 100}ms` }">
            <div class="hs-card__icon">
              <component :is="card.icon" />
            </div>
            <h3 class="hs-card__title">{{ card.title }}</h3>
            <p class="hs-card__body">{{ card.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── PRINCIPLES ─────────────────────────────────────────── -->
    <section ref="principlesSection" class="hs-section">
      <div class="hs-container">
        <div class="hs-section__header"
             :class="{ 'is-visible': isVisible.principles }">
          <h2 class="hs-heading">Core Principles</h2>
          <p class="hs-body-lg">
            Six foundational principles that transform how drivers think, act, and respond on the road.
          </p>
        </div>
        <div class="hs-cards-3">
          <div v-for="(p, i) in principles" :key="i"
               class="hs-card hs-card--lined"
               :class="{ 'is-visible': isVisible.principles }"
               :style="{ transitionDelay: `${i * 80}ms` }">
            <span class="hs-card__index">{{ String(i + 1).padStart(2, '0') }}</span>
            <h3 class="hs-card__title">{{ p.title }}</h3>
            <p class="hs-card__body">{{ p.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── QST ────────────────────────────────────────────────── -->
    <section ref="qstSection" class="hs-section hs-section--dark">
      <div class="hs-container">
        <div class="hs-section__header hs-section__header--light"
             :class="{ 'is-visible': isVisible.qst }">
          <h2 class="hs-heading">Question-Structured Techniques</h2>
          <p class="hs-body-lg">
            Mental prompts that activate safe driving, engage critical thinking, and transform conscious decisions into automatic habits.
          </p>
        </div>
        <div class="hs-qst-list">
          <div v-for="(q, i) in qstQuestions" :key="i"
               class="hs-qst-item"
               :class="{ 'is-visible': isVisible.qst }"
               :style="{ transitionDelay: `${i * 80}ms` }">
            <span class="hs-qst-item__num">{{ String(i + 1).padStart(2, '0') }}</span>
            <div>
              <h3 class="hs-qst-item__title">{{ q.title }}</h3>
              <p class="hs-qst-item__body">{{ q.description }}</p>
            </div>
          </div>
        </div>
        <div class="hs-qst-powers"
             :class="{ 'is-visible': isVisible.qst }"
             style="transition-delay: 500ms">
          <h3 class="hs-qst-powers__title">The Power of QST</h3>
          <div class="hs-qst-powers__grid">
            <div v-for="(pw, i) in qstPowers" :key="i" class="hs-qst-powers__item">
              <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="hs-check">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              <span><strong>{{ pw.label }}:</strong> {{ pw.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── HABIT LOOP ─────────────────────────────────────────── -->
    <section ref="habitSection" class="hs-section hs-section--alt">
      <div class="hs-container">
        <div class="hs-section__header"
             :class="{ 'is-visible': isVisible.habit }">
          <h2 class="hs-heading">From Awareness to Instinct</h2>
          <p class="hs-body-lg">
            HABITSAFE transforms conscious safety decisions into automatic reflexes through the science of habit formation.
          </p>
        </div>
        <div class="hs-loop" :class="{ 'is-visible': isVisible.habit }">
          <template v-for="(step, i) in habitLoop" :key="i">
            <div class="hs-loop__step"
                 :class="`hs-loop__step--${i}`"
                 :style="{ transitionDelay: `${i * 150}ms` }">
              <!-- glow ring -->
              <div class="hs-loop__ring" :class="`hs-loop__ring--${i}`"></div>
              <div class="hs-loop__node" :class="`hs-loop__node--${i}`">
                <svg v-if="i === 0" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
                </svg>
                <svg v-if="i === 1" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                </svg>
                <svg v-if="i === 2" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 class="hs-loop__label">{{ step.label }}</h3>
              <p class="hs-loop__desc">{{ step.desc }}</p>
            </div>

            <!-- Animated connector -->
            <div v-if="i < habitLoop.length - 1" class="hs-loop__connector"
                 :class="{ 'is-visible': isVisible.habit }"
                 :style="{ transitionDelay: `${i * 150 + 200}ms` }">
              <div class="hs-loop__connector-line">
                <div class="hs-loop__connector-dot"></div>
              </div>
              <svg class="hs-loop__connector-arrow" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </div>
          </template>
        </div>
        <div class="hs-loop__equation" :class="{ 'is-visible': isVisible.habit }" style="transition-delay:300ms">
          HABITSAFE = Habit + Safety + Feedback Loop
          <span class="hs-loop__tagline">Safe Actions → Safe Habits → Safe Roads</span>
        </div>
      </div>
    </section>

    <!-- ── COMPARISON ─────────────────────────────────────────── -->
    <section ref="comparisonSection" class="hs-section">
      <div class="hs-container">
        <div class="hs-section__header"
             :class="{ 'is-visible': isVisible.comparison }">
          <h2 class="hs-heading">HABITSAFE vs. Conventional Systems</h2>
          <p class="hs-body-lg">See how HABITSAFE transforms every dimension of driver training and safety culture.</p>
        </div>
        <div class="hs-table-wrap" :class="{ 'is-visible': isVisible.comparison }">
          <table class="hs-table">
            <thead>
              <tr>
                <th>Aspect</th>
                <th>
                  <span class="hs-table__badge">HABITSAFE</span>
                </th>
                <th>Conventional</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in comparisonData" :key="i">
                <td class="hs-table__aspect">{{ row.aspect }}</td>
                <td class="hs-table__good">
                  <div class="hs-table__cell-inner">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                    {{ row.habitsafe }}
                  </div>
                </td>
                <td class="hs-table__bad">
                  <div class="hs-table__cell-inner">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                    {{ row.conventional }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ── OUTCOMES ───────────────────────────────────────────── -->
    <section ref="outcomesSection" class="hs-section hs-section--dark">
      <div class="hs-container">
        <div class="hs-section__header hs-section__header--light"
             :class="{ 'is-visible': isVisible.outcomes }">
          <h2 class="hs-heading">Expected Outcomes & Impact</h2>
          <p class="hs-body-lg">Making safety automatic — even under stress, fatigue, or distraction.</p>
        </div>
        <div class="hs-cards-3">
          <div v-for="(o, i) in outcomes" :key="i"
               class="hs-card hs-card--dark"
               :class="{ 'is-visible': isVisible.outcomes }"
               :style="{ transitionDelay: `${i * 80}ms` }">
            <component :is="o.icon" class="hs-card__icon-sm" />
            <h3 class="hs-card__title">{{ o.title }}</h3>
            <p class="hs-card__body">{{ o.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── ALIGNMENT ─────────────────────────────────────────── -->
    <section ref="alignmentSection" class="hs-section hs-section--alt">
      <div class="hs-container">
        <div class="hs-section__header"
             :class="{ 'is-visible': isVisible.alignment }">
          <h2 class="hs-heading">Global Alignment & Strategic Value</h2>
          <p class="hs-body-lg">HABITSAFE embeds behavioral safety into recognized global standards and frameworks.</p>
        </div>
        <div class="hs-align-grid" :class="{ 'is-visible': isVisible.alignment }">
          <div v-for="(frame, i) in frameworks" :key="i"
               class="hs-align-card"
               :style="{ transitionDelay: `${i * 100}ms` }">
            <span class="hs-align-card__label">{{ frame.label }}</span>
            <ul>
              <li v-for="item in frame.items" :key="item">{{ item }}</li>
            </ul>
          </div>
        </div>
        <div class="hs-strategy" :class="{ 'is-visible': isVisible.alignment }" style="transition-delay:350ms">
          <h3 class="hs-strategy__title">Strategic Value</h3>
          <div class="hs-strategy__grid">
            <div v-for="sv in strategicValues" :key="sv" class="hs-strategy__item">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              {{ sv }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CTA ────────────────────────────────────────────────── -->
    <section class="hs-cta">
      <div class="hs-container">
        <div class="hs-cta__inner" :class="{ 'is-visible': isVisible.cta }">
          <h2 class="hs-cta__heading">Ready to Transform Your Safety Culture?</h2>
          <p class="hs-cta__body">
            HABITSAFE is more than training — it's a movement toward safer roads, empowered drivers, and harmonized standards worldwide.
          </p>
          <router-link to="/contact" class="hs-btn hs-btn--primary hs-btn--lg">
            Start Your Journey
            <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
          <div class="hs-cta__tags">
            <span v-for="tag in ctaTags" :key="tag" class="hs-cta__tag">
              <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              {{ tag }}
            </span>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

// ── Visibility ──────────────────────────────────────────────────
const isVisible = ref({
  hero: false, stats: false, challenge: false, principles: false,
  qst: false, habit: false, comparison: false, outcomes: false,
  alignment: false, cta: false
})

// ── Section refs ────────────────────────────────────────────────
const challengeSection   = ref(null)
const principlesSection  = ref(null)
const qstSection         = ref(null)
const habitSection       = ref(null)
const comparisonSection  = ref(null)
const outcomesSection    = ref(null)
const alignmentSection   = ref(null)

const scrollToSection = (el) => el?.scrollIntoView({ behavior: 'smooth' })

// ── Inline SVG icon components (must be declared before data arrays that reference them) ──
const WarningIcon  = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg>` }
const DocIcon      = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>` }
const LightningIcon = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/></svg>` }
const BulbIcon     = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"/></svg>` }
const TriggerIcon  = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/></svg>` }
const RoutineIcon  = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/></svg>` }
const RewardIcon   = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>` }
const ShieldIcon   = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/></svg>` }
const DollarIcon   = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>` }
const HeartIcon    = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>` }
const StarIcon     = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/></svg>` }
const GlobeIcon    = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253M3 12a8.959 8.959 0 011.016-4.165"/></svg>` }
const RocketIcon   = { template: `<svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/></svg>` }

// ── Data ────────────────────────────────────────────────────────
const stats = [
  { value: '90%',  label: 'Human Error in Crashes' },
  { value: '5',    label: 'Core QST Questions' },
  { value: '100%', label: 'Behavior-Driven' },
]

const challengeCards = [
  {
    icon: WarningIcon,
    title: 'Human Error Dominates',
    body: 'Over 90% of incidents are caused by human error, not vehicle failure.'
  },
  {
    icon: DocIcon,
    title: 'Rule-Based Systems Fail',
    body: 'Conventional systems are disengaging, culturally rigid, and lack improvement tools.'
  },
  {
    icon: LightningIcon,
    title: 'Awareness is Power',
    body: 'The brain processes visual cues in milliseconds — faster than any vehicle moves.'
  },
  {
    icon: BulbIcon,
    title: 'New Approach Needed',
    body: 'A behavior-centered framework that makes safety instinctive, not optional.'
  },
]

const principles = [
  { title: 'Behavior-Driven',    description: 'Focuses on human perception, judgment, and accountability — not just rules.' },
  { title: 'Question Structured', description: 'Uses reflective QST methodology to build critical thinking and lasting change.' },
  { title: 'Culturally Rooted',  description: 'Adapts to local traditions and linguistic practices for greater relevance.' },
  { title: 'Inclusive Design',   description: 'Embeds gender sensitivity and protection for vulnerable road users.' },
  { title: 'Globally Aligned',   description: 'Complies with ISO standards, UN SDGs, and ADR regulations.' },
  { title: 'Built for Growth',   description: 'Includes audit-ready tools, revision clauses, and continuous improvement.' },
]

const qstQuestions = [
  { title: 'What is this technique?',  description: 'Conceptual clarity that builds confidence and relevance in safety practices.' },
  { title: 'Why is it necessary?',     description: 'Purpose-driven reflection that creates emotional ownership of safety.' },
  { title: 'How do I apply it?',       description: 'Links theory to practice, creating habits that stick through real-world use.' },
  { title: "What's the challenge?",    description: 'Develops problem-solving mindset and heightens risk awareness.' },
  { title: "What's the takeaway?",     description: 'Reflective memory recall that reinforces habits and improves long-term retention.' },
]

const qstPowers = [
  { label: 'Critical Thinking',  text: 'Guided questioning deepens reflection beyond memorization' },
  { label: 'Problem-Solving',    text: "Learners' own questions reveal gaps and shape solutions" },
  { label: 'Retention',          text: 'Retrieving answers through questioning strengthens memory' },
  { label: 'Engagement',         text: 'Question exchanges motivate and enhance teamwork' },
]

const habitLoop = [
  { icon: TriggerIcon, label: 'Trigger', desc: 'Question sparks awareness' },
  { icon: RoutineIcon, label: 'Routine', desc: 'Action becomes automatic' },
  { icon: RewardIcon,  label: 'Reward',  desc: 'Safety reinforces the loop' },
]

const comparisonData = [
  { aspect: 'Pedagogy',   habitsafe: 'Reflective QST',                    conventional: 'Rule-based lectures' },
  { aspect: 'Cultural Fit', habitsafe: 'Locally inspired',                conventional: 'Standardized content' },
  { aspect: 'Inclusivity', habitsafe: 'Embedded by design',               conventional: 'Optional or absent' },
  { aspect: 'Compliance',  habitsafe: 'Globally aligned (ISO, UN, ADR)',   conventional: 'Fragmented or reactive' },
  { aspect: 'ROI',         habitsafe: 'Strategic investment',              conventional: 'Regulatory obligation' },
  { aspect: 'Tools',       habitsafe: 'Customizable, bilingual, audit-ready', conventional: 'Static modules' },
]

const outcomes = [
  { icon: ShieldIcon,  title: 'Reduced Crash Rates',       description: 'Automatic safety responses prevent incidents before they happen.' },
  { icon: DollarIcon,  title: 'Lower Insurance Costs',     description: 'Proven safety culture translates to reduced premiums and claims.' },
  { icon: HeartIcon,   title: 'Improved Driver Retention', description: 'Empowered drivers feel valued and stay longer with organizations.' },
  { icon: StarIcon,    title: 'Operational Excellence',    description: 'Consistent safety practices improve efficiency across all operations.' },
  { icon: GlobeIcon,   title: 'Harmonized Culture',        description: 'Unified safety standards across borders and diverse workforces.' },
  { icon: RocketIcon,  title: 'Strategic Advantage',       description: 'Compliance becomes culture, creating lasting competitive benefits.' },
]

const frameworks = [
  {
    label: 'ISO',
    items: ['45001 — Workplace Safety', '31000 — Risk Accountability', '26000 — Ethical Culture', '21448/26262 — Human Factors']
  },
  {
    label: 'UN SDGs',
    items: ['SDG 3 — Health & Safety', 'SDG 8 — Decent Work', 'SDG 11 — Safe Cities', 'SDG 12 — Responsible Operations']
  },
  {
    label: 'ADR',
    items: ['Disciplined Driver Behavior', 'Emergency Readiness', 'Crew Training', 'Hazardous Goods Handling']
  },
]

const strategicValues = [
  'Transforms compliance into culture',
  'Reduces organizational risk',
  'Strengthens accountability',
  'Builds legacy-driven safety systems',
]

const ctaTags = ['Bilingual (EN/FR)', 'Audit-Ready', 'Globally Aligned', 'Continuous Improvement']

// ── Intersection Observer ───────────────────────────────────────
let observer = null

onMounted(() => {
  setTimeout(() => {
    isVisible.value.hero  = true
    isVisible.value.stats = true
  }, 100)

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const key = entry.target.dataset.section
          if (key) isVisible.value[key] = true
        }
      })
    },
    { threshold: 0.08 }
  )

  const map = {
    challenge: challengeSection,
    principles: principlesSection,
    qst: qstSection,
    habit: habitSection,
    comparison: comparisonSection,
    outcomes: outcomesSection,
    alignment: alignmentSection,
  }

  for (const [key, ref] of Object.entries(map)) {
    if (ref.value) {
      ref.value.dataset.section = key
      observer.observe(ref.value)
    }
  }
})

onUnmounted(() => observer?.disconnect())
</script>

<style scoped>
/* ── Tokens ──────────────────────────────────────────────────── */
:root {
  --hs-black:   #1d1d1f;
  --hs-gray:    #6e6e73;
  --hs-gray-lt: #d2d2d7;
  --hs-bg:      #f5f5f7;
  --hs-white:   #ffffff;
  --hs-amber:   #d97706;
}

/* ── Base ────────────────────────────────────────────────────── */
.hs-page {
  font-family: -apple-system, 'SF Pro Display', 'Inter', sans-serif;
  color: #1d1d1f;
  background: #fff;
  overflow-x: hidden;
}

.hs-container {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ── Typography ──────────────────────────────────────────────── */
.hs-display {
  font-size: clamp(64px, 10vw, 112px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
  color: #1d1d1f;
}

.hs-heading {
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: #1d1d1f;
  margin: 0 0 16px;
}

.hs-eyebrow {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6e6e73;
  margin: 0 0 24px;
}

.hs-body-lg {
  font-size: 18px;
  line-height: 1.65;
  color: #6e6e73;
  max-width: 600px;
  margin: 0 auto;
}

/* ── Hero ────────────────────────────────────────────────────── */
.hs-hero {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 120px 32px 80px;
  background: #06060a;
  position: relative;
  overflow: hidden;
}

/* Orb animations */
.hs-hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hs-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0;
  animation: orbFadeIn 2s ease forwards;
}

.hs-orb--1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(99,102,241,.18) 0%, transparent 70%);
  top: -15%;
  left: -10%;
  animation-delay: 0.2s;
  animation: orbFadeIn 2s ease 0.2s forwards, orbFloat1 18s ease-in-out 2.2s infinite;
}

.hs-orb--2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(217,119,6,.12) 0%, transparent 70%);
  top: 20%;
  right: -5%;
  animation: orbFadeIn 2s ease 0.5s forwards, orbFloat2 22s ease-in-out 2.5s infinite;
}

.hs-orb--3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(139,92,246,.14) 0%, transparent 70%);
  bottom: 10%;
  left: 25%;
  animation: orbFadeIn 2s ease 0.8s forwards, orbFloat3 20s ease-in-out 2.8s infinite;
}

.hs-orb--4 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(20,184,166,.1) 0%, transparent 70%);
  top: 50%;
  left: 60%;
  animation: orbFadeIn 2s ease 1s forwards, orbFloat2 25s ease-in-out 3s infinite reverse;
}

/* Subtle dot-grid overlay */
.hs-hero__grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
}

@keyframes orbFadeIn {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
}

@keyframes orbFloat1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(40px, -30px) scale(1.05); }
  66%       { transform: translate(-20px, 20px) scale(0.97); }
}

@keyframes orbFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  40%       { transform: translate(-30px, 40px) scale(1.04); }
  70%       { transform: translate(20px, -20px) scale(0.98); }
}

@keyframes orbFloat3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(25px, -35px) scale(1.06); }
}

.hs-hero__inner {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.9s cubic-bezier(.22,.68,0,1.2), transform 0.9s cubic-bezier(.22,.68,0,1.2);
  position: relative;
  z-index: 1;
}

.hs-hero__inner.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-hero .hs-eyebrow {
  color: rgba(255,255,255,.4);
}

.hs-hero .hs-display {
  color: #fff;
}

.hs-hero__sub {
  font-size: clamp(17px, 2vw, 22px);
  line-height: 1.55;
  color: rgba(255,255,255,.5);
  max-width: 560px;
  margin: 20px auto 44px;
}

.hs-hero__actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.hs-hero__scroll-hint {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

.hs-hero__scroll-hint span {
  display: block;
  width: 1px;
  height: 56px;
  background: linear-gradient(to bottom, transparent, rgba(255,255,255,.25));
  margin: 0 auto;
  animation: scrollPulse 2s ease-in-out infinite;
}

@keyframes scrollPulse {
  0%, 100% { opacity: 0.3; transform: scaleY(0.6); transform-origin: top; }
  50%       { opacity: 1;   transform: scaleY(1);   transform-origin: top; }
}

/* ── Buttons ─────────────────────────────────────────────────── */
.hs-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 100px;
  padding: 12px 24px;
  transition: all 0.25s ease;
  cursor: pointer;
  border: none;
  text-decoration: none;
  white-space: nowrap;
}

.hs-btn--primary {
  background: #1d1d1f;
  color: #fff;
}

.hs-btn--primary:hover {
  background: #3d3d3f;
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0,0,0,.18);
}

.hs-btn--primary:hover svg {
  transform: translateX(3px);
}

.hs-btn--primary svg {
  transition: transform 0.2s ease;
}

.hs-btn--ghost {
  background: transparent;
  color: #1d1d1f;
  border: 1.5px solid #d2d2d7;
}

.hs-btn--ghost:hover {
  border-color: #6e6e73;
  background: #f5f5f7;
}

.hs-btn--ghost-hero {
  color: rgba(255,255,255,.8);
  border-color: rgba(255,255,255,.18);
  background: rgba(255,255,255,.05);
  backdrop-filter: blur(12px);
}

.hs-btn--ghost-hero:hover {
  background: rgba(255,255,255,.12);
  border-color: rgba(255,255,255,.35);
  color: #fff;
}

.hs-btn--lg {
  font-size: 17px;
  padding: 16px 36px;
}

/* ── Stats ───────────────────────────────────────────────────── */
.hs-stats {
  padding: 64px 0;
  border-top: 1px solid #f2f2f2;
  border-bottom: 1px solid #f2f2f2;
}

.hs-stats__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
}

.hs-stats__grid > :not(:last-child) {
  border-right: 1px solid #e8e8ed;
}

.hs-stat {
  text-align: center;
  padding: 0 40px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.hs-stat.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-stat__num {
  display: block;
  font-size: 52px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1d1d1f;
  line-height: 1;
  margin-bottom: 8px;
}

.hs-stat__label {
  font-size: 14px;
  color: #6e6e73;
  font-weight: 400;
  letter-spacing: 0.01em;
}

/* ── Sections ────────────────────────────────────────────────── */
.hs-section {
  padding: 100px 0;
}

.hs-section--alt {
  background: #f5f5f7;
}

.hs-section--dark {
  background: #1d1d1f;
  color: #fff;
}

.hs-section--dark .hs-heading {
  color: #fff;
}

.hs-section__header {
  text-align: center;
  margin-bottom: 64px;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.hs-section__header.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-section__header--light .hs-body-lg {
  color: rgba(255,255,255,.55);
}

/* ── Cards ───────────────────────────────────────────────────── */
.hs-cards-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.hs-cards-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .hs-cards-2,
  .hs-cards-3 { grid-template-columns: 1fr; }
  .hs-stats__grid { grid-template-columns: 1fr; }
  .hs-stats__grid > :not(:last-child) { border-right: none; border-bottom: 1px solid #e8e8ed; padding-bottom: 32px; margin-bottom: 32px; }
}

.hs-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease, transform 0.6s ease, box-shadow 0.25s ease;
}

.hs-card:hover {
  box-shadow: 0 12px 40px rgba(0,0,0,.08);
  transform: translateY(-2px) !important;
}

.hs-card.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-card--lined {
  background: transparent;
  border: 1px solid #e8e8ed;
}

.hs-card--lined:hover {
  background: #fff;
  border-color: transparent;
  box-shadow: 0 12px 40px rgba(0,0,0,.07);
}

.hs-card--dark {
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.08);
}

.hs-card--dark:hover {
  background: rgba(255,255,255,.09);
  box-shadow: none;
}

.hs-card--dark .hs-card__title { color: #fff; }
.hs-card--dark .hs-card__body  { color: rgba(255,255,255,.5); }

.hs-card__icon {
  width: 44px;
  height: 44px;
  color: #1d1d1f;
  margin-bottom: 20px;
  opacity: 0.75;
}

.hs-card__icon svg,
.hs-card__icon-sm svg {
  width: 100%;
  height: 100%;
}

.hs-card__icon-sm {
  width: 32px;
  height: 32px;
  color: rgba(255,255,255,.6);
  margin-bottom: 16px;
}

.hs-card__index {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #a1a1a6;
  margin-bottom: 12px;
}

.hs-card__title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #1d1d1f;
  margin: 0 0 8px;
}

.hs-card__body {
  font-size: 15px;
  line-height: 1.6;
  color: #6e6e73;
  margin: 0;
}

/* ── QST ─────────────────────────────────────────────────────── */
.hs-qst-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 48px;
}

.hs-qst-item {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 24px 28px;
  border-radius: 14px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.07);
  opacity: 0;
  transform: translateX(-20px);
  transition: opacity 0.55s ease, transform 0.55s ease, background 0.2s;
}

.hs-qst-item:hover {
  background: rgba(255,255,255,.08);
}

.hs-qst-item.is-visible {
  opacity: 1;
  transform: translateX(0);
}

.hs-qst-item__num {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,.3);
  padding-top: 3px;
  flex-shrink: 0;
}

.hs-qst-item__title {
  font-size: 17px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
}

.hs-qst-item__body {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255,255,255,.45);
  margin: 0;
}

.hs-qst-powers {
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 20px;
  padding: 36px 40px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.hs-qst-powers.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-qst-powers__title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255,255,255,.35);
  margin: 0 0 20px;
}

.hs-qst-powers__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.hs-qst-powers__item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(255,255,255,.6);
}

.hs-qst-powers__item strong { color: rgba(255,255,255,.85); }

.hs-check {
  flex-shrink: 0;
  color: rgba(255,255,255,.3);
  margin-top: 2px;
}

/* ── Habit Loop ──────────────────────────────────────────────── */
.hs-loop {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 56px;
  background: #fff;
  border-radius: 28px;
  border: 1px solid #e8e8ed;
  margin-bottom: 20px;
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.hs-loop.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-loop.is-visible .hs-loop__step {
  opacity: 1;
  transform: translateY(0);
}

.hs-loop__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  flex: 1;
  min-width: 160px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}

/* Per-step accent colors */
.hs-loop__node--0 { background: #eff6ff; color: #3b82f6; }
.hs-loop__node--1 { background: #fefce8; color: #ca8a04; }
.hs-loop__node--2 { background: #f0fdf4; color: #16a34a; }

.hs-loop__node--0:hover { background: #dbeafe; }
.hs-loop__node--1:hover { background: #fef9c3; }
.hs-loop__node--2:hover { background: #dcfce7; }

/* Glow ring behind node */
.hs-loop__ring {
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% calc(-50% - 50px);
  width: 88px;
  height: 88px;
  border-radius: 50%;
  opacity: 0;
  z-index: 0;
}

.hs-loop.is-visible .hs-loop__ring { animation: ringPulse 3s ease-in-out infinite; }
.hs-loop__ring--0 { background: rgba(59,130,246,.12); animation-delay: 0s !important; }
.hs-loop__ring--1 { background: rgba(202,138,4,.1);   animation-delay: 1s !important; }
.hs-loop__ring--2 { background: rgba(22,163,74,.1);   animation-delay: 2s !important; }

@keyframes ringPulse {
  0%, 100% { transform: scale(1);    opacity: 0; }
  50%       { transform: scale(1.55); opacity: 1; }
}

.hs-loop__node {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
  transition: transform 0.3s cubic-bezier(.34,1.56,.64,1), box-shadow 0.3s ease;
  cursor: default;
}

.hs-loop__node:hover {
  transform: scale(1.1) translateY(-3px);
  box-shadow: 0 12px 32px rgba(0,0,0,.1);
}

.hs-loop__node svg {
  width: 28px;
  height: 28px;
  stroke: currentColor;
}

.hs-loop__label {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #1d1d1f;
  margin: 0 0 6px;
}

.hs-loop__desc {
  font-size: 13px;
  line-height: 1.5;
  color: #6e6e73;
  margin: 0;
  max-width: 120px;
}

/* Animated connector between steps */
.hs-loop__connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-bottom: 46px;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.hs-loop__connector.is-visible {
  opacity: 1;
}

.hs-loop__connector-line {
  width: 56px;
  height: 2px;
  background: #e8e8ed;
  border-radius: 1px;
  position: relative;
  overflow: hidden;
}

.hs-loop__connector-dot {
  position: absolute;
  top: 50%;
  left: -12px;
  translate: 0 -50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #a1a1a6;
}

.hs-loop.is-visible .hs-loop__connector-dot {
  animation: dotSlide 2s ease-in-out 0.8s infinite;
}

@keyframes dotSlide {
  0%   { left: -12px; opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { left: calc(100% + 4px); opacity: 0; }
}

.hs-loop__connector-arrow {
  color: #c7c7cc;
  display: none;
}

.hs-loop__equation {
  text-align: center;
  padding: 22px 32px;
  border-radius: 16px;
  background: #f5f5f7;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  letter-spacing: -0.01em;
  display: flex;
  flex-direction: column;
  gap: 5px;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.hs-loop__equation.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-loop__tagline {
  font-size: 13px;
  font-weight: 400;
  color: #6e6e73;
}

@media (max-width: 600px) {
  .hs-loop {
    flex-direction: column;
    padding: 40px 32px;
    gap: 32px;
  }
  .hs-loop__connector { flex-direction: row; margin-bottom: 0; }
  .hs-loop__connector-line { width: 2px; height: 40px; }
}

/* ── Table ───────────────────────────────────────────────────── */
.hs-table-wrap {
  overflow-x: auto;
  border-radius: 20px;
  border: 1px solid #e8e8ed;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.hs-table-wrap.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.hs-table thead tr {
  background: #1d1d1f;
  color: #fff;
}

.hs-table th {
  padding: 18px 24px;
  text-align: left;
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.02em;
}

.hs-table__badge {
  display: inline-block;
  padding: 3px 10px;
  background: rgba(217,119,6,.2);
  color: #f59e0b;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.hs-table tbody tr {
  border-top: 1px solid #f2f2f2;
  transition: background 0.15s;
}

.hs-table tbody tr:hover {
  background: #fafafa;
}

.hs-table td {
  padding: 16px 24px;
  vertical-align: middle;
}

.hs-table__aspect {
  font-weight: 500;
  color: #1d1d1f;
}

.hs-table__good { color: #1d1d1f; }
.hs-table__bad  { color: #6e6e73; }

.hs-table__cell-inner {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hs-table__good .hs-table__cell-inner svg { color: #16a34a; flex-shrink: 0; }
.hs-table__bad  .hs-table__cell-inner svg { color: #dc2626; flex-shrink: 0; }

/* ── Alignment ───────────────────────────────────────────────── */
.hs-align-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .hs-align-grid { grid-template-columns: 1fr; }
  .hs-qst-powers__grid { grid-template-columns: 1fr; }
}

.hs-align-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 20px;
  padding: 32px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease, box-shadow 0.25s ease;
}

.hs-align-grid.is-visible .hs-align-card {
  opacity: 1;
  transform: translateY(0);
}

.hs-align-card:nth-child(1) { transition-delay: 0ms; }
.hs-align-card:nth-child(2) { transition-delay: 100ms; }
.hs-align-card:nth-child(3) { transition-delay: 200ms; }

.hs-align-card:hover {
  box-shadow: 0 10px 32px rgba(0,0,0,.07);
}

.hs-align-card__label {
  display: block;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1d1d1f;
  margin-bottom: 20px;
}

.hs-align-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hs-align-card li {
  font-size: 14px;
  color: #6e6e73;
  line-height: 1.5;
  padding-left: 14px;
  position: relative;
}

.hs-align-card li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d2d2d7;
}

.hs-strategy {
  background: #1d1d1f;
  border-radius: 20px;
  padding: 40px;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.hs-strategy.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-strategy__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,.35);
  margin: 0 0 20px;
}

.hs-strategy__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

@media (max-width: 600px) {
  .hs-strategy__grid { grid-template-columns: 1fr; }
}

.hs-strategy__item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: rgba(255,255,255,.75);
}

.hs-strategy__item svg { color: rgba(255,255,255,.3); flex-shrink: 0; }

/* ── CTA ─────────────────────────────────────────────────────── */
.hs-cta {
  padding: 120px 0;
  background: #fff;
  border-top: 1px solid #f2f2f2;
}

.hs-cta__inner {
  text-align: center;
  max-width: 680px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.hs-cta__inner.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hs-cta__heading {
  font-size: clamp(28px, 4vw, 48px);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: #1d1d1f;
  margin: 0 0 16px;
}

.hs-cta__body {
  font-size: 17px;
  line-height: 1.65;
  color: #6e6e73;
  margin: 0 0 40px;
}

.hs-cta__tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
}

.hs-cta__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6e6e73;
  background: #f5f5f7;
  padding: 6px 14px;
  border-radius: 100px;
}

.hs-cta__tag svg { color: #a1a1a6; }
</style>
